import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getDirectory, getPattis, createDirectoryEntry } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Search, Phone, User, Briefcase, MapPin, Loader2, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const DirectoryPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialPatti = searchParams.get('patti') || 'all';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatti, setSelectedPatti] = useState(initialPatti);
  const [entries, setEntries] = useState([]);
  const [pattis, setPattis] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Submission form state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newEntry, setNewEntry] = useState({
    name: '', phone: '', patti_id: 1, occupation: ''
  });

  // Fetch pattis on mount
  useEffect(() => {
    const fetchPattis = async () => {
      try {
        const data = await getPattis();
        setPattis(data);
      } catch (error) {
        console.error('Error fetching pattis:', error);
      }
    };
    fetchPattis();
  }, []);

  // Fetch directory entries when patti changes
  useEffect(() => {
    const fetchDirectory = async () => {
      setLoading(true);
      try {
        const pattiId = selectedPatti === 'all' ? null : parseInt(selectedPatti);
        const data = await getDirectory(pattiId);
        setEntries(data);
      } catch (error) {
        console.error('Error fetching directory:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDirectory();
  }, [selectedPatti]);

  // Filter entries by search term (client-side)
  const filteredEntries = useMemo(() => {
    if (!searchTerm) return entries;
    
    return entries.filter(entry => {
      const matchesSearch = entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           entry.phone.includes(searchTerm) ||
                           entry.occupation.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [entries, searchTerm]);

  const getPattiName = (pattiId) => {
    const patti = pattis.find(p => p.id === pattiId);
    if (!patti) return '';
    return language === 'pa' ? patti.name_pa : patti.name_en;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.name || !newEntry.phone || !newEntry.occupation) {
      toast.error(language === 'pa' ? 'ਸਾਰੇ ਖੇਤਰ ਭਰੋ' : 'Please fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      await createDirectoryEntry(newEntry, false); // false = public submission (pending)
      setSubmitted(true);
      setTimeout(() => {
        setSubmitOpen(false);
        setSubmitted(false);
        setNewEntry({ name: '', phone: '', patti_id: 1, occupation: '' });
      }, 2000);
    } catch (error) {
      toast.error(language === 'pa' ? 'ਗਲਤੀ ਹੋਈ' : 'Error submitting');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਪਿੰਡ ਡਾਇਰੈਕਟਰੀ' : 'Village Directory'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪੱਟੀ ਅਨੁਸਾਰ ਪਿੰਡ ਵਾਸੀਆਂ ਦੀ ਡਾਇਰੈਕਟਰੀ' : 'Directory of village residents by Patti'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Patti Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <Button
            variant={selectedPatti === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedPatti('all')}
            className={selectedPatti === 'all' ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-300 hover:bg-amber-50'}
          >
            {t('allPattis')}
          </Button>
          {pattis.map((patti) => (
            <Button
              key={patti.id}
              variant={selectedPatti === String(patti.id) ? 'default' : 'outline'}
              onClick={() => setSelectedPatti(String(patti.id))}
              className={selectedPatti === String(patti.id) ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-300 hover:bg-amber-50'}
            >
              {language === 'pa' ? patti.name_pa : patti.name_en}
            </Button>
          ))}
        </div>

        {/* Search & Filter */}
        <Card className="border-0 shadow-md mb-8">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('searchDirectory')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="md:hidden">
                <Select value={selectedPatti} onValueChange={setSelectedPatti}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('selectPatti')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allPattis')}</SelectItem>
                    {pattis.map((patti) => (
                      <SelectItem key={patti.id} value={String(patti.id)}>
                        {language === 'pa' ? patti.name_pa : patti.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-500">
            {language === 'pa' 
              ? `${filteredEntries.length} ਨਤੀਜੇ ਮਿਲੇ`
              : `${filteredEntries.length} results found`}
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <>
            {/* Directory Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEntries.map((entry) => (
                <Card key={entry.id} className="group border-0 shadow-md hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">
                          {entry.name}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <MapPin className="h-3 w-3" />
                          {getPattiName(entry.patti_id)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                      <a 
                        href={`tel:${entry.phone}`}
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors"
                      >
                        <Phone className="h-4 w-4" />
                        {entry.phone}
                      </a>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Briefcase className="h-4 w-4" />
                        {entry.occupation}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredEntries.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <User className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {language === 'pa' ? 'ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ ਮਿਲਿਆ' : 'No results found'}
                </h3>
                <p className="text-gray-500">
                  {language === 'pa' ? 'ਕਿਰਪਾ ਵੱਖਰੇ ਖੋਜ ਸ਼ਬਦ ਵਰਤੋ' : 'Try a different search term'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Add Button */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogTrigger asChild>
          <Button 
            className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 shadow-lg hover:shadow-xl transition-all duration-300 z-50"
            size="icon"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'pa' ? 'ਸਫਲਤਾਪੂਰਵਕ ਜਮ੍ਹਾਂ!' : 'Submitted Successfully!'}
              </h3>
              <p className="text-gray-500 text-sm">
                {language === 'pa' 
                  ? 'ਐਡਮਿਨ ਦੀ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                  : 'Will be visible after admin approval'}
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {language === 'pa' ? 'ਡਾਇਰੈਕਟਰੀ ਵਿੱਚ ਸ਼ਾਮਲ ਕਰੋ' : 'Add to Directory'}
                </DialogTitle>
                <DialogDescription>
                  {language === 'pa' 
                    ? 'ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਹੀ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                    : 'Entry will appear after admin approval'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <Input 
                  placeholder={language === 'pa' ? 'ਨਾਮ *' : 'Name *'}
                  value={newEntry.name}
                  onChange={(e) => setNewEntry({...newEntry, name: e.target.value})}
                />
                <Input 
                  placeholder={language === 'pa' ? 'ਫ਼ੋਨ ਨੰਬਰ *' : 'Phone Number *'}
                  value={newEntry.phone}
                  onChange={(e) => setNewEntry({...newEntry, phone: e.target.value})}
                />
                <Select 
                  value={String(newEntry.patti_id)} 
                  onValueChange={(val) => setNewEntry({...newEntry, patti_id: parseInt(val)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'pa' ? 'ਪੱਟੀ ਚੁਣੋ' : 'Select Patti'} />
                  </SelectTrigger>
                  <SelectContent>
                    {pattis.map((patti) => (
                      <SelectItem key={patti.id} value={String(patti.id)}>
                        {language === 'pa' ? patti.name_pa : patti.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  placeholder={language === 'pa' ? 'ਕਿੱਤਾ *' : 'Occupation *'}
                  value={newEntry.occupation}
                  onChange={(e) => setNewEntry({...newEntry, occupation: e.target.value})}
                />
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setSubmitOpen(false)}
                  >
                    {language === 'pa' ? 'ਰੱਦ ਕਰੋ' : 'Cancel'}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'pa' ? 'ਜਮ੍ਹਾਂ ਕਰੋ' : 'Submit')}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectoryPage;
